import { useState } from 'react';
import Image from 'next/image';
import Crown from '../../public/static/crown_v3.svg';
import { ProductResponse } from '../../types/products/product-response';
import MostPopularHeader from './MostPopularHeader';
import BestDealHeader from './BestDealHeader';
import { useRecoilState } from 'recoil';
import { isOpenSignUpModalState } from '../../common/atoms/checkout';
import dynamic from 'next/dynamic';
import { SessionStatusEnum } from '../../common/enums/session-status.enum';
import { useSession } from 'next-auth/react';
import { useUser } from '../../hooks/users/useUser';
import { useLocalizedPrice } from '../../hooks/currencies/useLocalizedPrice';
import { formatAmountWithCurrency } from '../../common/utils/currency.utils';
import { toast } from 'react-toastify';
import { trackPostHogEvent } from '../../common/utils/posthog.utils';
import LightningDealHeader from './LightningDealHeader';
import LightningDealFooter from './LightningDealFooter';
import { LootBoxRarityEnum } from '../../types/loot-box/loot-box.enums';
import Tooltip from '../Tooltip';
import LootBoxTooltip from '../loot-boxes/LootBoxTooltip';

const PackageConfirmationModal = dynamic(
  () => import('./modal/PackageConfirmationModal'),
  {
    ssr: true,
  },
)

type Props = {
  product: ProductResponse
  isPopular?: boolean
  isBestDeal?: boolean
  isLightningDeal?: boolean
  lightningDealExpiresAt?: Date
}

export default function PackageCard({ product, isPopular, isBestDeal, isLightningDeal, lightningDealExpiresAt }: Props) {
  const [openPackageConfirmationModal, setOpenPackageConfirmationModal] =
    useState(false)
  const [, setIsOpenSignUp] = useRecoilState(isOpenSignUpModalState)
  const { status } = useSession()
  const { user } = useUser()
  const { getLocalizedPrice } = useLocalizedPrice()
  const localizedPrice = getLocalizedPrice(product?.localizedPrices?.charge)
  const localizedCredits = getLocalizedPrice(
    product?.localizedCreditAmount?.charge,
  )
  const localizedBonusCredits = getLocalizedPrice(
    product?.localizedBonusCreditAmount?.charge,
  )

  const rarityDisplayOrder = [
    LootBoxRarityEnum.RARE,
    LootBoxRarityEnum.EPIC,
    LootBoxRarityEnum.LEGENDARY,
  ]

  const rarityLabelMap: Record<LootBoxRarityEnum, string> = {
    [LootBoxRarityEnum.RARE]: 'Rare',
    [LootBoxRarityEnum.EPIC]: 'Epic',
    [LootBoxRarityEnum.LEGENDARY]: 'Legendary',
  }

  const rarityClassMap: Record<LootBoxRarityEnum, string> = {
    [LootBoxRarityEnum.RARE]: 'text-blue-400',
    [LootBoxRarityEnum.EPIC]: 'text-purple-400',
    [LootBoxRarityEnum.LEGENDARY]: 'text-orange-500',
  }

  return (
    <div
      className={
        'bg1 text-color-gray5 w-96 rounded-xl transition-transform hover:z-50 hover:scale-105 flex flex-col'
      }
    >
      <div className="flex flex-col flex-grow">
        {isPopular && <MostPopularHeader />}
        {isBestDeal && <BestDealHeader />}
        {isLightningDeal && lightningDealExpiresAt &&
          <LightningDealHeader displayText={product.title}/>
        }

        <div className={'mx-8 mb-10 flex flex-col items-center flex-grow'}>
          <div
            className={`relative mt-10 ${isBestDeal ? 'visible' : 'invisible'}`}
          >
            <Image
              width={32}
              height={24}
              src={Crown}
              className={'object-contain'}
              alt={'Crown icon'}
            />
          </div>

          {/* Credits */}
          <div
            className={'mt-1 flex flex-row items-center justify-center gap-2'}
          >
            <p className={'text-color-gray5 header5'}>
              {formatAmountWithCurrency(
                localizedCredits?.price,
                localizedCredits?.currency,
              )}
              {' Credits'}
            </p>
          </div>
          
          {/* BONUS Credits */}
          {(localizedBonusCredits?.price ?? 0) > 0 && (
            <label
              className={' text-[#9184FF] text-body-medium'}
            >
              {`+${formatAmountWithCurrency(
                localizedBonusCredits?.price,
                localizedBonusCredits?.currency,
              )} bonus credits`}
            </label>
          )}

          {/* LOOT BOX allocations */}
          {product.lootBoxAllocations && product.lootBoxAllocations.length > 0 && (
            <div className={'mt-3 flex flex-col items-center gap-1'}>
              <p className="text-body-medium text-white mb-1">
                Plus Exclusive Loot Boxes
              </p>
              {/* Sort to always show Rare -> Epic -> Legendary */}
              {product.lootBoxAllocations
                .slice()
                .sort((a, b) =>
                  rarityDisplayOrder.indexOf(a.rarity) -
                  rarityDisplayOrder.indexOf(b.rarity),
                )
                .map((allocation) => (
                <Tooltip
                  content={
                    <LootBoxTooltip
                      rarity={allocation.rarity}
                      strict={allocation.strictRarity}
                    />
                  }
                >
                <p
                  key={allocation.id}
                  className={'text-body-medium inline-block border-b border-dashed border-current pb-[1px]'}
                >
                  {allocation.quantity}{' '}
                  <span className={`${rarityClassMap[allocation.rarity]}`}>
                    {rarityLabelMap[allocation.rarity]}
                    {!allocation.strictRarity && '+'}
                    {' Loot Box'}
                    {allocation.quantity > 1 ? 'es' : ''
                  }</span>
                </p>
                </Tooltip>
              ))}
            </div>
          )}

          {/* Bottom block - always sticks to the bottom of the card for alignment. */}
          <div className="flex flex-col items-center w-full mt-auto">
            <button
              className={
                'text-secondary-bold btn mt-12 flex h-12 w-full rounded-lg border-none bg-gradient-to-r from-[#6C98FF] to-[#7636FF] text-white shadow-xl hover:opacity-80'
              }
              onClick={(e) => {
                e.preventDefault()
                if (status === SessionStatusEnum.UNAUTHENTICATED) {
                  setIsOpenSignUp(true)
                  return
                }
                if (user?.isGuest) {
                  toast.error(
                    "Apologies, but in order to reload your balance, you'll need to create an account.",
                  )
                  return
                }
                setOpenPackageConfirmationModal(true)
                // Track user click on credits package
                trackPostHogEvent('User click on credits package', {
                  id: product.id,
                  title: product.title,
                  amount: formatAmountWithCurrency(
                    localizedPrice?.price,
                    localizedPrice?.currency,
                    2,
                  ),
                })
              }}
            >
              Buy •
              {formatAmountWithCurrency(
                localizedPrice?.price,
                localizedPrice?.currency,
                2,
              )}
            </button>

            {/* Footer */}
            {isLightningDeal && lightningDealExpiresAt && (
              <LightningDealFooter expiresAt={lightningDealExpiresAt} />
            )}
          </div>
        </div>

        {openPackageConfirmationModal && (
          <PackageConfirmationModal
            title={'Confirm Your Purchase'}
            message={`${formatAmountWithCurrency(
              (localizedCredits?.price ?? 0) +
                (localizedBonusCredits?.price ?? 0),
              localizedCredits?.currency,
              2,
            )} credits will be added to your account.`}
            openModal={openPackageConfirmationModal}
            openCallback={(closed) => {
              setOpenPackageConfirmationModal(closed)
            }}
            product={product}
            from={'reload'}
            enableKlarna={user?.id === 25487 || user?.id === 3}
          />
        )}
      </div>
    </div>
  )
}
